import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, ChevronRight, X, Zap } from "lucide-react";

const DISMISSED_KEY = "fl_setup_checklist_dismissed";

/**
 * First-week setup checklist for new orgs. Shows the office a short
 * to-do list (logo, pay-week, team, client, job, fleet, billing). Each
 * item links to the right page. The card auto-hides once every
 * non-optional step is done — or when the user explicitly dismisses it.
 *
 * Office/admin only. Field workers don't see this.
 */
export default function SetupChecklist() {
  const { user } = useAuth();
  const isOffice = user?.role && user.role !== "field";

  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(DISMISSED_KEY) === "1";
  });

  useEffect(() => {
    if (!isOffice || dismissed) { setLoaded(true); return; }
    let cancelled = false;
    api.get("/setup/checklist")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { /* silently fail — checklist is non-critical */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [isOffice, dismissed]);

  if (!isOffice || !loaded || !data || dismissed || data.completed) return null;

  const pct = data.total ? Math.round((data.done / data.total) * 100) : 0;
  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "1");
    setDismissed(true);
  };

  // Cockpit fonts used inline for the slashy industrial look.
  const fontHead = { fontFamily: "'Barlow Condensed', system-ui, sans-serif" };

  return (
    <div
      className="rounded-sm border border-zinc-300 dark:border-zinc-800 overflow-hidden relative bg-white dark:bg-[#101620] mb-6"
      data-testid="setup-checklist"
    >
      {/* danger-stripe header */}
      <div className="px-5 py-3 flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-[#151C27]">
        <div className="flex items-center gap-2 min-w-0">
          <Zap className="w-4 h-4 text-[#FF5F15] shrink-0" />
          <div
            className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] truncate"
            style={{ ...fontHead, fontWeight: 700 }}
          >
            // Mission · first-week setup
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-baseline gap-1.5">
            <div className="text-[22px] leading-none tabular-nums text-zinc-900 dark:text-white" style={{ ...fontHead, fontWeight: 800 }}>
              {pct}%
            </div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500" style={{ ...fontHead, fontWeight: 700 }}>
              {data.done}/{data.total}
            </div>
          </div>
          <button
            onClick={dismiss}
            className="text-zinc-400 hover:text-zinc-900 dark:hover:text-white"
            aria-label="Dismiss setup checklist"
            data-testid="setup-checklist-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* glow progress bar */}
      <div className="px-5 pt-4">
        <div className="h-1 rounded-full overflow-hidden bg-zinc-200 dark:bg-[#262626]">
          <div
            className="h-full transition-all duration-700"
            style={{
              width: `${pct}%`,
              background: "#FF5F15",
              boxShadow: "0 0 12px rgba(255, 95, 21, 0.35)",
            }}
          />
        </div>
      </div>

      {/* steps grid */}
      <ul className="p-4 grid grid-cols-1 md:grid-cols-2 gap-2">
        {data.items.map((item) => (
          <ChecklistRow key={item.key} item={item} />
        ))}
      </ul>

      <div className="px-5 py-3 border-t border-zinc-200 dark:border-zinc-800 text-[11px] text-zinc-500 flex items-center justify-between flex-wrap gap-2">
        <span>
          Not sure where to start? Read the{" "}
          <Link to="/app/help" className="text-[#FF5F15] font-bold hover:underline">Welcome guide</Link> first.
        </span>
      </div>
    </div>
  );
}

function ChecklistRow({ item }) {
  const fontHead = { fontFamily: "'Barlow Condensed', system-ui, sans-serif" };
  return (
    <li>
      <Link
        to={item.to}
        className="flex items-center gap-3 px-3 py-2.5 rounded-sm border transition-colors group"
        style={{
          borderColor: item.done ? "transparent" : "#FF5F15",
          background: item.done ? "transparent" : "rgba(255, 95, 21, 0.06)",
        }}
        data-testid={`setup-checklist-row-${item.key}`}
      >
        <div
          className="w-4 h-4 rounded-sm flex items-center justify-center shrink-0 border"
          style={{
            background: item.done ? "#FF5F15" : "transparent",
            borderColor: "#FF5F15",
            boxShadow: item.done ? "0 0 8px rgba(255, 95, 21, 0.35)" : "none",
          }}
        >
          {item.done && <CheckCircle2 className="w-3 h-3 text-black" strokeWidth={3} />}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className={`text-[12px] uppercase tracking-[0.04em] ${item.done ? "text-zinc-500 line-through" : "text-zinc-900 dark:text-white"}`}
            style={{ ...fontHead, fontWeight: item.done ? 600 : 800 }}
          >
            {item.label}
            {item.optional && !item.done && (
              <span className="ml-2 text-[9px] tracking-widest text-zinc-500 align-middle" style={fontHead}>· OPTIONAL</span>
            )}
          </div>
          {item.blurb && !item.done && (
            <div
              className="text-[10px] uppercase tracking-[0.1em] mt-0.5 text-zinc-500 dark:text-zinc-400"
              style={{ ...fontHead, fontWeight: 700 }}
            >
              {item.blurb}
            </div>
          )}
        </div>
        {!item.done && (
          <span
            className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm bg-[#FF5F15] text-black shrink-0"
            style={{ ...fontHead, fontWeight: 700 }}
          >
            Go →
          </span>
        )}
        {item.done && (
          <ChevronRight className="w-3.5 h-3.5 text-zinc-400 shrink-0 opacity-0 group-hover:opacity-100" />
        )}
      </Link>
    </li>
  );
}
